// File: src/App.js
// Purpose: Main application component. Handles auth state and renders pages.

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'; // Import router components
import { jwtDecode } from 'jwt-decode';
import { Provider } from 'react-redux'; // Add Redux Provider
import { HelmetProvider } from 'react-helmet-async'; // Add Helmet Provider
import { loadStripe } from '@stripe/stripe-js'; // Import Stripe
import store from './store/store'; // Import Redux store
import { setupGlobalErrorHandling, logAuthenticationChange } from './utils/cloudLogger'; // Import error handling and auth logging
import { useNavigationLogger } from './hooks/useNavigationLogger'; // Import navigation logger
import { isFeatureEnabled, FEATURES, logFeatureFlags } from './utils/featureFlags'; // Import feature flags
import PerformanceMonitor from './components/organisms/PerformanceMonitor'; // Import performance monitor
import GoogleTagManager from './components/GoogleTagManager'; // Import Google Tag Manager

// Import conversion tracking test utilities for development
if (process.env.REACT_APP_DEPLOYMENT_ENV === 'development') {
  import('./utils/conversionTrackingTest');
  import('./utils/testRateCheckerWorkflow');
}

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ConsolePage = lazy(() => import('./pages/ConsolePage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ProhibitedItemsPage = lazy(() => import('./pages/ProhibitedItemsPage'));
const CloudLoggingTest = lazy(() => import('./components/CloudLoggingTest'));

// Lazy load SEO Landing Pages
const USPSShippingPage = lazy(() => import('./pages/USPSShippingPage'));
const UPSShippingPage = lazy(() => import('./pages/UPSShippingPage'));
const FedExShippingPage = lazy(() => import('./pages/FedExShippingPage'));
const DHLShippingPage = lazy(() => import('./pages/DHLShippingPage'));
const ShippingCalculatorPage = lazy(() => import('./pages/ShippingCalculatorPage'));
const SmallBusinessShippingPage = lazy(() => import('./pages/SmallBusinessShippingPage'));
const InternationalShippingPage = lazy(() => import('./pages/InternationalShippingPage'));
const RateCheckerPage = lazy(() => import('./pages/RateCheckerPage'));
const UspsUpsComparisonPage = lazy(() => import('./pages/UspsUpsComparisonPage'));
const HowToPackageBoxPage = lazy(() => import('./pages/HowToPackageBoxPage'));
const ShippingToCanadaPage = lazy(() => import('./pages/ShippingToCanadaPage'));
const EcommerceShippingPage = lazy(() => import('./pages/EcommerceShippingPage'));
const OvernightShippingPage = lazy(() => import('./pages/OvernightShippingPage'));
const BulkShipmentWizard = lazy(() => import('./components/pages/BulkShipmentWizard'));
const TestResponsive = lazy(() => import('./pages/TestResponsive'));

// Lazy load Additional SEO Landing Pages
const ShippingRatesPage = lazy(() => import('./pages/ShippingRatesPage'));
const ShippingLabelsPage = lazy(() => import('./pages/ShippingLabelsPage'));
const DiscountedShippingPage = lazy(() => import('./pages/DiscountedShippingPage'));
const BulkShippingPage = lazy(() => import('./pages/BulkShippingPage'));
const ShippingGuidePage = lazy(() => import('./pages/ShippingGuidePage'));

// Lazy load Blog Pages
const BlogsPage = lazy(() => import('./pages/BlogsPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const EcommerceCallbackPage = lazy(() => import('./pages/EcommerceCallbackPage'));

import Spinner from './components/atoms/Spinner'; // Import Spinner component
import ToastContainer from './components/molecules/ToastContainer'; // Global toast notifications

// Setup global error handling
setupGlobalErrorHandling();

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Helper component for protected routes
const ProtectedRoute = ({ user, children }) => {
  if (!user) {
    console.debug('[ProtectedRoute] No user found, redirecting to /login');
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [isAuthCheckComplete, setIsAuthCheckComplete] = useState(false);
  const [activeSection, setActiveSection] = useState('home'); // Default to 'home' or based on initial route

  // --- Initialize feature flags on app start ---
  useEffect(() => {
    logFeatureFlags();
  }, []);

  // --- Check for token on initial load ---
  useEffect(() => {
    console.debug('[App] useEffect - Checking for existing auth token...');
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decodedUser = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedUser.exp < currentTime) {
          console.warn('[App] Token expired, clearing token.');
          localStorage.removeItem('authToken');
          setUser(null);
        } else {
          console.debug('[App] Valid token found, fetching complete user profile.');
          // Extract basic user info from token for immediate use
          const basicUserInfo = {
            id: decodedUser.sub || decodedUser.userId,
            email: decodedUser.email,
            name: decodedUser.name,
          };
          setUser(basicUserInfo);
          
          // Fetch complete user profile including address
          fetchUserProfile(token);
        }
      } catch (error) {
        console.error('[App] Error decoding token:', error);
        localStorage.removeItem('authToken');
        setUser(null);
      }
    } else {
       console.debug('[App] No auth token found.');
       setUser(null);
    }
    setIsAuthCheckComplete(true);
  }, []);

  // Function to fetch complete user profile
  const fetchUserProfile = async (token) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.debug('[App] User profile fetched successfully:', data.user);
        setUser(data.user);
        
        // Log authentication restoration
        logAuthenticationChange('login', {
          ...data.user,
          method: 'token_restoration'
        });
      } else {
        console.error('[App] Failed to fetch user profile, clearing token.');
        localStorage.removeItem('authToken');
        setUser(null);
      }
    } catch (error) {
      console.error('[App] Error fetching user profile:', error);
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  // --- Auth Handlers (modified for navigation) ---
  // Use a wrapper component to access useNavigate hook
  const AuthHandler = () => {
    const navigate = useNavigate();
    const { logSectionChange } = useNavigationLogger(); // Add navigation logging

    const handleAuthSuccess = async (loggedInUser) => {
      console.debug('[App] handleAuthSuccess called with user:', loggedInUser);
      
      // If we have a token, fetch complete profile including address
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetchUserProfile(token);
      } else {
        // Fallback to basic user info if no token
        setUser(loggedInUser);
      }
      
      // Check for redirect parameters from Rate Checker
      const urlParams = new URLSearchParams(window.location.search);
      const redirectPath = urlParams.get('redirect');
      const redirectSection = urlParams.get('section');
      
      if (redirectPath && redirectSection) {
        console.debug('[App] Found redirect parameters:', { redirectPath, redirectSection });
        setActiveSection(redirectSection);
        
        // Log successful authentication with redirect context
        logAuthenticationChange('login', {
          ...loggedInUser,
          method: 'interactive_login',
          redirectContext: { redirectPath, redirectSection }
        });
        
        // Clear URL parameters and navigate to target section
        window.history.replaceState({}, '', redirectPath);
        navigate(redirectPath);
      } else {
        // Normal login flow
        setActiveSection('welcome');
        
        // Log successful authentication
        logAuthenticationChange('login', {
          ...loggedInUser,
          method: 'interactive_login'
        });
        
        console.debug('[App] Navigating to /app after successful auth.');
        navigate('/app');
      }
    };

    const handleLogout = () => {
      console.debug('[App] handleLogout called');
      
      // Log logout before removing token
      logAuthenticationChange('logout', {
        reason: 'user_initiated'
      });
      
      localStorage.removeItem('authToken');
      setUser(null);
      setActiveSection('home'); // Set active section to home after logout
      console.debug('[App] Navigating to / after logout.');
      navigate('/'); // Navigate to home page after logout
    };

    // --- NEW: Navigation handler for Sidebar ---
    const handleSidebarNavigation = (path, sectionId) => {
      console.log(`[App] Sidebar navigating to path: ${path}, section: ${sectionId}`);
      navigate(path);
      if (sectionId) {
        setActiveSection(sectionId);
        logSectionChange(sectionId, { 
          source: 'sidebar',
          path,
          sectionId 
        });
      } else {
        // Infer section from path if no explicit sectionId
        if (path === '/') setActiveSection('home');
        else if (path === '/login') setActiveSection('login');
        else if (path === '/faq') setActiveSection('faq');
        else if (path.startsWith('/app/account')) setActiveSection('account'); // <-- ADD for account
        else if (path.startsWith('/app/analytics')) setActiveSection('analytics'); // Add for analytics
        else if (path.startsWith('/app/ecommerce-integrations')) setActiveSection('ecommerce-integrations'); // Add for eCommerce integrations
        else if (path.startsWith('/app/connect-accounts')) setActiveSection('connect-accounts'); // Add for connect accounts
        else if (path.startsWith('/app/seller-orders')) setActiveSection('seller-orders'); // Add for seller orders
        else if (path.startsWith('/app/listings')) setActiveSection('listings'); // Add for listings
        else if (path.startsWith('/app/payment')) setActiveSection('payment'); // Add for payment
        else if (path.startsWith('/catalog')) setActiveSection('catalog'); // Catalog (Phase A/B/C)
        else if (path.startsWith('/app')) setActiveSection('welcome'); // Default for /app
        // Add more specific path to section mappings if needed
      }
    };
    // --- END NEW ---

    // Pass handlers down to relevant components
    // Note: LoginPage needs onAuthSuccess
    // ConsolePage needs user, onLogout, activeSection, and onSidebarNavigate

    return (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Spinner /></div>}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/faq" element={<FAQPage />} />

        {/* Login Route */}
        <Route
          path="/login"
          element={user ? <Navigate to="/app" replace /> : <LoginPage onAuthSuccess={handleAuthSuccess} />}
        />

        {/* --- Route for Terms Page --- */}
        <Route path="/terms" element={<TermsPage />} />
        {/* --- End Route --- */}

        {/* --- Route for Privacy Page --- */}
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* --- End Route --- */}

        {/* --- NEW: Route for About Page --- */}
        <Route path="/about" element={<AboutPage />} />
        {/* --- END NEW --- */}

        {/* --- Route for Cloud Logging Test (Development/Debug) --- */}
        <Route path="/cloud-logging-test" element={<CloudLoggingTest />} />
        {/* --- End Route --- */}

        {/* --- Route for Responsive Test (Development/Debug) --- */}
        <Route path="/test-responsive" element={<TestResponsive />} />
        {/* --- End Route --- */}

        {/* --- Route for Prohibited Items Page --- */}
        <Route path="/prohibited-items" element={<ProhibitedItemsPage />} />
        {/* --- End Route --- */}

        {/* SEO Landing Pages */}
        <Route path="/usps-shipping" element={<USPSShippingPage />} />
        <Route path="/ups-shipping" element={<UPSShippingPage />} />
        <Route path="/fedex-shipping" element={<FedExShippingPage />} />
        <Route path="/dhl-shipping" element={<DHLShippingPage />} />
        <Route path="/shipping-calculator" element={<ShippingCalculatorPage />} />
        <Route path="/small-business-shipping" element={<SmallBusinessShippingPage />} />
        <Route path="/international-shipping" element={<InternationalShippingPage />} />
        <Route path="/ecommerce-shipping-solutions" element={<EcommerceShippingPage />} />
        <Route path="/overnight-shipping-rates" element={<OvernightShippingPage />} />

        {/* Additional SEO Landing Pages */}
        <Route path="/shipping-rates" element={<ShippingRatesPage />} />
        <Route path="/shipping-labels" element={<ShippingLabelsPage />} />
        <Route path="/discounted-shipping" element={<DiscountedShippingPage />} />
        <Route path="/bulk-shipping" element={<BulkShippingPage />} />
        <Route path="/shipping-guide" element={<ShippingGuidePage />} />

        {/* Blog Routes - Only available if feature flag is enabled */}
        {isFeatureEnabled(FEATURES.BLOGS) && (
          <>
            <Route path="/blog" element={<BlogsPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
          </>
        )}

        {/* New SEO Content Pages */}
        <Route path="/usps-priority-vs-ups-ground" element={<UspsUpsComparisonPage />} />
        <Route path="/how-to-package-a-box" element={<HowToPackageBoxPage />} />
        <Route path="/shipping-to-canada" element={<ShippingToCanadaPage />} />

        {/* Rate Checker Page - Public, no authentication required */}
        <Route path="/check-rates" element={<RateCheckerPage />} />

        {/* eCommerce OAuth Callback Routes - Protected routes */}
        <Route
          path="/api/ecommerce-integrations/callback/shopify"
          element={
            <ProtectedRoute user={user}>
              <EcommerceCallbackPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/api/ecommerce-integrations/callback/ebay"
          element={
            <ProtectedRoute user={user}>
              <EcommerceCallbackPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/api/ecommerce-integrations/callback/etsy"
          element={
            <ProtectedRoute user={user}>
              <EcommerceCallbackPage />
            </ProtectedRoute>
          }
        />

        {/* Catalog (Phase A) — Protected route. Gated behind REACT_APP_ENABLE_CATALOG.
            Server side also gates behind ENABLE_CATALOG=true; both must be on for end-to-end. */}
        {process.env.REACT_APP_ENABLE_CATALOG === 'true' && (
          <Route
            path="/catalog"
            element={
              <ProtectedRoute user={user}>
                <CatalogPage />
              </ProtectedRoute>
            }
          />
        )}

        {/* Wallet (store credit funded via Zelle) — gated behind REACT_APP_ENABLE_ZELLE_FUNDING.
            Server also gates behind ENABLE_ZELLE_FUNDING=true; both must be on end-to-end. */}
        {process.env.REACT_APP_ENABLE_ZELLE_FUNDING === 'true' && (
          <Route
            path="/wallet"
            element={
              <ProtectedRoute user={user}>
                <WalletPage />
              </ProtectedRoute>
            }
          />
        )}

        {/* Bulk Shipment Wizard - Protected route - Only available if feature flag is enabled */}
        {isFeatureEnabled(FEATURES.BULK_SHIPMENTS) && (
          <Route
            path="/bulk-shipments"
            element={
              <ProtectedRoute user={user}>
                <BulkShipmentWizard 
                  stripePromise={stripePromise}
                />
              </ProtectedRoute>
            }
          />
        )}

        {/* Protected App Route - This will render ConsolePage which then renders other sections */}
        <Route
          path="/app/*" // Use /* to allow nested routes within ConsolePage if needed later
          element={
            <ProtectedRoute user={user}>
              <ConsolePage
                user={user}
                onLogout={handleLogout}
                activeSection={activeSection}
                onSidebarNavigate={handleSidebarNavigation} // Pass the new handler
                onUserUpdate={setUser} // Pass setUser function to update user state
              />
            </ProtectedRoute>
          }
        />

        {/* Fallback for unknown routes (optional) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    );
  };

  // --- Render Logic ---
  if (!isAuthCheckComplete) {
      console.debug('[App] Initial auth check in progress...');
      return <div className="flex items-center justify-center h-screen"><Spinner /></div>; // Full page spinner
  }

  return (
    <PerformanceMonitor>
      <HelmetProvider>
        <Provider store={store}>
          <GoogleTagManager>
            <BrowserRouter>
              <AuthHandler />
            </BrowserRouter>
            <ToastContainer />
          </GoogleTagManager>
        </Provider>
      </HelmetProvider>
    </PerformanceMonitor>
  );
}

export default App;